import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Plus, Trash2, Utensils, Search, Sparkles, ShieldCheck, RefreshCw, Upload } from 'lucide-react';
import { moduleApi } from '../../../lib/api';
import { Button } from '../../ui/Button';
import { categoryCompare } from '../../../lib/categoryOrder';
import { allergensOfIngredient } from '../../../../shared/allergenReference.js';
import type { DishVariant, DishIngredient } from './MenuCatalogEditors';
import { MenuCatalogAudit } from './MenuCatalogAudit';
import { MenuCatalogImport } from './MenuCatalogImport';
import { StatusFilterChips } from '../../ui/StatusFilterChips';
import { formulaDietBadges } from './formulaDietBadges';
import { DishCard, FormulaCard, DishBadgeLegend } from './MenuCatalogCards';
import { MenuDishForm } from './MenuDishForm';
import { MenuFormulaForm } from './MenuFormulaForm';
import { CatalogModalShell } from './CatalogModalShell';
import type { MenuItem, MenuStructureCategory, Menu } from './menuCatalogTypes';


const emptyDishForm = () => ({
    name: '', category: '', description: '',
    ingredients: [] as DishIngredient[], allergens: [] as string[], dietary_tags: [] as string[], incompatible_tags: [] as string[],
    variants: [] as DishVariant[], traces: [] as string[],
});

const emptyMenuForm = () => ({ name: '', structure: [] as MenuStructureCategory[] });

/** Module "Menu & Plats" (déplacé hors d'Événementiel — demande explicite d'Emeni 2026-08-22 : "les
 *  paramètres menu et plat ne doivent pas être dans le module événementiel, ils doivent être dans le module
 *  menu et plat", "ce paramètre doit ouvrir une page pas une modale") : catalogue de plats et de formules
 *  réutilisable d'un événement à l'autre. Les ingrédients sont en texte libre (pas une liste fermée
 *  d'allergènes) pour que le moteur de compatibilité puisse repérer n'importe quel ingrédient déclaré par un
 *  convive, pas seulement les 14 allergènes réglementaires. */
export function MenuCatalogPage() {
    const navigate = useNavigate();
    const [items, setItems] = React.useState<MenuItem[]>([]);
    const [menus, setMenus] = React.useState<Menu[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [tab, setTab] = React.useState<'plats' | 'formules'>('plats');
    const [search, setSearch] = React.useState('');
    // Filtre par catégorie du catalogue (Entrée / Plat / Dessert...). Avec 27 plats, retrouver les desserts
    // imposait de faire défiler toute la carte. Demande d'Emeni (2026-08-24).
    const [categoryFilter, setCategoryFilter] = React.useState('all');

    const [importingTraces, setImportingTraces] = React.useState(false);
    const [convertingVariants, setConvertingVariants] = React.useState(false);
    const [importNotice, setImportNotice] = React.useState('');
    // Termes personnalisés de l'établissement : un ingrédient rattaché à la main doit être reconnu comme
    // configuré, sinon la fiche continuerait de le signaler à tort.
    const [allergenTerms, setAllergenTerms] = React.useState<any[]>([]);
    const [allergenNames, setAllergenNames] = React.useState<string[]>([]);
    const [auditOpen, setAuditOpen] = React.useState(false);
    const [importOpen, setImportOpen] = React.useState(false);
    const [resetting, setResetting] = React.useState(false);

    const [dishFormOpen, setDishFormOpen] = React.useState(false);
    const [editingDishId, setEditingDishId] = React.useState<string | null>(null);
    const [dishForm, setDishForm] = React.useState(emptyDishForm());
    const [savingDish, setSavingDish] = React.useState(false);

    const [menuFormOpen, setMenuFormOpen] = React.useState(false);
    const [editingMenuId, setEditingMenuId] = React.useState<string | null>(null);
    const [menuForm, setMenuForm] = React.useState(emptyMenuForm());
    const [savingMenu, setSavingMenu] = React.useState(false);

    // "silent" : rechargement sans repasser par l'écran de chargement. Après avoir enregistré un plat, le
    // spinner vidait la liste, la page se raccourcissait et le navigateur ramenait le scroll en haut — on
    // perdait sa place après chaque modification, très pénible sur 23 plats. Signalé par Emeni (2026-08-23).
    const load = React.useCallback((silent = false) => {
        if (!silent) setLoading(true);
        Promise.all([
            moduleApi.getMenuItems().catch(() => []),
            moduleApi.getMenus().catch(() => []),
            moduleApi.getAllergenTerms().catch(() => null),
        ]).then(([i, m, a]: any[]) => {
            setItems(Array.isArray(i) ? i : []);
            setMenus(Array.isArray(m) ? m : []);
            const terms = [
                ...((a?.custom || []).map((t: any) => ({ family: t.family, term: t.term, kind: 'term' }))),
                ...((a?.labels || []).map((t: any) => ({ family: t.family, term: t.term, kind: 'label' }))),
                ...((a?.disabled || []).map((t: any) => ({ family: t.family, term: t.term, kind: 'term', disabled: true }))),
            ];
            setAllergenTerms(terms);
            setAllergenNames([...(a?.builtin || []), ...(a?.restrictions || [])].map((g: any) => g.canonical).sort((x: string, y: string) => x.localeCompare(y, 'fr')));
        }).finally(() => setLoading(false));
    }, []);

    React.useEffect(() => { load(); }, [load]);

    const knownCategories = React.useMemo(() => {
        const set = new Set<string>();
        menus.forEach((m) => m.structure.forEach((s) => { if (s.category) set.add(s.category); }));
        items.forEach((i) => { if (i.category) set.add(i.category); });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr'));
    }, [menus, items]);

    const knownRestrictions = React.useMemo(() => {
        const set = new Set<string>();
        items.forEach((i) => {
            (i.dietary_tags || []).forEach((t) => set.add(t));
            (i.incompatible_tags || []).forEach((t) => set.add(t));
            (i.variants || []).forEach((v) => (v.resolves_restrictions || []).forEach((r) => set.add(r)));
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr'));
    }, [items]);

    // Un seul crible de recherche pour la liste ET pour les compteurs des chips : recopié, il aurait dérivé
    // au premier champ ajouté, et les chips auraient annoncé 8 desserts pour n'en afficher que 3.
    const searchedItems = React.useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((i) => i.name.toLowerCase().includes(q) || i.category.toLowerCase().includes(q)
            || i.ingredients.some((ing) => ing.name.toLowerCase().includes(q))
            || (i.traces || []).some((t) => t.toLowerCase().includes(q)));
    }, [items, search]);

    const filteredItems = React.useMemo(() => {
        const byCategory = categoryFilter === 'all' ? searchedItems : searchedItems.filter((i) => i.category === categoryFilter);
        return byCategory.slice().sort((a, b) => categoryCompare(a.category, b.category) || a.name.localeCompare(b.name, 'fr'));
    }, [searchedItems, categoryFilter]);

    // Les catégories proposées sont celles réellement présentes dans la carte, dans l'ordre du service :
    // afficher une catégorie vide n'aurait aucun sens, et une carte sans dessert ne doit pas en inventer un.
    const dishById = React.useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

    const categoryOptions = React.useMemo(() => {
        const counts = new Map<string, number>();
        searchedItems.forEach((i) => counts.set(i.category, (counts.get(i.category) || 0) + 1));
        return Array.from(counts.entries())
            .sort((a, b) => categoryCompare(a[0], b[0]))
            .map(([category, count]) => ({ value: category, label: category, count }));
    }, [searchedItems]);

    const filteredMenus = React.useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return menus;
        return menus.filter((m) => m.name.toLowerCase().includes(q));
    }, [menus, search]);

    // Applique en une fois la liste de traces fournie par Emeni pour la carte des 20 plats. Le compte rendu
    // dit explicitement quels plats du catalogue n'ont PAS été couverts, pour qu'un plat sans traces ne passe
    // pas inaperçu — c'est précisément le cas où un convive allergique ne serait pas prévenu.
    const importTraces = async () => {
        if (!window.confirm("Appliquer la liste des traces aux plats de la carte ? Les traces déjà saisies sur ces plats seront remplacées.")) return;
        setImportingTraces(true);
        setError('');
        setImportNotice('');
        try {
            const res: any = await moduleApi.importMenuItemTraces();
            const parts = [`${res.updated_count} plat(s) mis à jour.`];
            if (res.not_found?.length) parts.push(`Introuvables dans ton catalogue : ${res.not_found.join(', ')}.`);
            if (res.untouched?.length) parts.push(`Sans traces (absents de la liste) : ${res.untouched.join(', ')}.`);
            setImportNotice(parts.join(' '));
            load();
        } catch (e: any) {
            setError(e?.message || "Erreur lors de l'import des traces.");
        } finally {
            setImportingTraces(false);
        }
    };

    // Convertit les anciennes variantes en ingrédients retirables. Ne s'affiche que tant qu'il reste des
    // variantes à traiter — une fois le catalogue basculé, le bouton disparaît de lui-même.
    const convertVariants = async () => {
        if (!window.confirm("Convertir les anciennes variantes en ingrédients retirables ?\n\nChaque « sans X » sera rapproché d'un ingrédient du plat, qui deviendra retirable. Les variantes dont un « sans X » ne correspond à aucun ingrédient seront conservées telles quelles et signalées.")) return;
        setConvertingVariants(true);
        setError('');
        setImportNotice('');
        try {
            const res: any = await moduleApi.convertMenuItemVariants();
            const parts = [`${res.converted_count} plat(s) converti(s), ${res.marked_count} ingrédient(s) rendu(s) retirable(s).`];
            if (res.converted?.length) parts.push(`Convertis : ${res.converted.join(' ; ')}.`);
            if (res.partial?.length) parts.push(`À traiter à la main : ${res.partial.join(' ; ')}.`);
            setImportNotice(parts.join(' '));
            load();
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la conversion.');
        } finally {
            setConvertingVariants(false);
        }
    };

    // Vide tout le catalogue pour repartir sur une nouvelle carte. Deux temps : on demande d'abord
    // l'impact au serveur (rien n'est supprimé), on le montre, puis on confirme. Les événements déjà
    // répondus perdent le libellé de leurs plats — autant le savoir avant de cliquer.
    const resetCatalog = async () => {
        setError(''); setImportNotice('');
        setResetting(true);
        try {
            const info: any = await moduleApi.resetMenuCatalog(false);
            const lignes = [`Supprimer DÉFINITIVEMENT ${info.dishes} plat(s) et ${info.menus} formule(s) ?`];
            if (info.impacted_events > 0) {
                lignes.push('', `⚠ ${info.impacted_events} événement(s) utilisent encore cette carte`
                    + (info.responded_guests > 0 ? `, dont ${info.responded_guests} convive(s) ayant déjà répondu.` : '.'));
                lignes.push("Leurs fiches cuisine ne retrouveront plus le nom des plats supprimés.");
            }
            lignes.push('', 'Cette action est irréversible.');
            if (!window.confirm(lignes.join('\n'))) return;
            const res: any = await moduleApi.resetMenuCatalog(true);
            setImportNotice(`Catalogue vidé : ${res.dishes} plat(s) et ${res.menus} formule(s) supprimés.`);
            load();
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la suppression.');
        } finally {
            setResetting(false);
        }
    };

    const resetDishForm = () => { setDishFormOpen(false); setEditingDishId(null); setDishForm(emptyDishForm()); };
    const startAddDish = () => { setEditingDishId(null); setDishForm(emptyDishForm()); setDishFormOpen(true); };
    const startEditDish = (item: MenuItem) => {
        setEditingDishId(item.id);
        setDishForm({
            name: item.name, category: item.category, description: item.description,
            ingredients: item.ingredients, allergens: item.allergens, dietary_tags: item.dietary_tags,
            incompatible_tags: item.incompatible_tags || [],
            variants: (item.variants || []).map((v) => ({ ...v })),
            traces: item.traces || [],
        });
        setDishFormOpen(true);
    };

    const saveDish = async () => {
        if (!dishForm.name.trim()) { setError('Le nom du plat est requis.'); return; }
        setSavingDish(true);
        setError('');
        try {
            // Les ids "tmp_..." des variantes tout juste ajoutées côté client sont retirés avant envoi — le
            // serveur en génère un définitif à l'enregistrement.
            const payload = {
                ...dishForm,
                variants: dishForm.variants.map((v) => ({
                    ...(v.id.startsWith('tmp_') ? {} : { id: v.id }),
                    label: v.label,
                    resolves_restrictions: v.resolves_restrictions,
                })),
            };
            if (editingDishId) await moduleApi.updateMenuItem(editingDishId, payload);
            else await moduleApi.createMenuItem(payload);
            resetDishForm();
            load(true);
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de l\'enregistrement du plat.');
        } finally {
            setSavingDish(false);
        }
    };

    const deleteDish = async (item: MenuItem) => {
        if (!window.confirm(`Supprimer le plat "${item.name}" ?`)) return;
        setError('');
        try {
            await moduleApi.deleteMenuItem(item.id);
            load(true);
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la suppression du plat.');
        }
    };

    const resetMenuForm = () => { setMenuFormOpen(false); setEditingMenuId(null); setMenuForm(emptyMenuForm()); };
    const startAddMenu = () => { setEditingMenuId(null); setMenuForm(emptyMenuForm()); setMenuFormOpen(true); };
    const startEditMenu = (menu: Menu) => {
        setEditingMenuId(menu.id);
        setMenuForm({ name: menu.name, structure: menu.structure.map((s) => ({ category: s.category, dish_ids: [...s.dish_ids] })) });
        setMenuFormOpen(true);
    };

    const addCategoryRow = () => setMenuForm((prev) => ({ ...prev, structure: [...prev.structure, { category: '', dish_ids: [] }] }));
    const updateCategoryRow = (idx: number, patch: Partial<MenuStructureCategory>) => setMenuForm((prev) => ({
        ...prev, structure: prev.structure.map((row, i) => i === idx ? { ...row, ...patch } : row),
    }));
    const removeCategoryRow = (idx: number) => setMenuForm((prev) => ({ ...prev, structure: prev.structure.filter((_, i) => i !== idx) }));
    const moveCategoryRow = (idx: number, dir: -1 | 1) => setMenuForm((prev) => {
        const next = [...prev.structure];
        const target = idx + dir;
        if (target < 0 || target >= next.length) return prev;
        [next[idx], next[target]] = [next[target], next[idx]];
        return { ...prev, structure: next };
    });
    const toggleDishInRow = (idx: number, dishId: string) => setMenuForm((prev) => ({
        ...prev, structure: prev.structure.map((row, i) => {
            if (i !== idx) return row;
            const has = row.dish_ids.includes(dishId);
            return { ...row, dish_ids: has ? row.dish_ids.filter((d) => d !== dishId) : [...row.dish_ids, dishId] };
        }),
    }));

    const saveMenu = async () => {
        if (!menuForm.name.trim()) { setError('Le nom de la formule est requis.'); return; }
        setSavingMenu(true);
        setError('');
        try {
            const payload = { name: menuForm.name.trim(), structure: menuForm.structure.filter((s) => s.category.trim()) };
            if (editingMenuId) await moduleApi.updateMenu(editingMenuId, payload);
            else await moduleApi.createMenu(payload);
            resetMenuForm();
            load(true);
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de l\'enregistrement de la formule.');
        } finally {
            setSavingMenu(false);
        }
    };

    const deleteMenu = async (menu: Menu) => {
        if (!window.confirm(`Supprimer la formule "${menu.name}" ?`)) return;
        setError('');
        try {
            await moduleApi.deleteMenu(menu.id);
            load(true);
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la suppression de la formule.');
        }
    };

    // Allergènes déduits d'un plat — même calcul que dans la fiche, pour que la carte et le formulaire
    // affichent exactement la même chose.
    const detectedFor = (item: MenuItem) => {
        const set = new Set<string>();
        (item.ingredients || []).forEach((i) => allergensOfIngredient(i, allergenTerms).forEach((f: string) => set.add(f)));
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr'));
    };

    // Rattache un ingrédient inconnu à un allergène, depuis la fiche plat. Le rattachement est global (il
    // vaut pour tous les plats), c'est bien l'intention : "Boudoir" contient du gluten partout.
    const linkAllergen = async (ingredient: string, allergen: string) => {
        setError('');
        try {
            await moduleApi.addAllergenTerm({ family: allergen, term: ingredient });
            const a: any = await moduleApi.getAllergenTerms();
            setAllergenTerms([
                ...((a?.custom || []).map((t: any) => ({ family: t.family, term: t.term, kind: 'term' }))),
                ...((a?.labels || []).map((t: any) => ({ family: t.family, term: t.term, kind: 'label' }))),
            ]);
        } catch (e: any) {
            setError(e?.message || 'Erreur lors du rattachement.');
        }
    };

    // La carte elle-même vit dans MenuCatalogCards.tsx : c'est de l'affichage pur, tout lui arrive en
    // propriété. Seul le calcul des allergènes déduits reste ici, il dépend du référentiel chargé.
    const renderDishCard = (item: MenuItem) => (
        <DishCard key={item.id} item={item} allergenTerms={allergenTerms} detected={detectedFor(item)}
            onEdit={startEditDish} onDelete={deleteDish} />
    );

    // Le formulaire du plat vit dans MenuDishForm.tsx, avec les trois calculs qui ne servent qu'à lui.
    const dishFormFields = (
        <MenuDishForm dishForm={dishForm} setDishForm={setDishForm} knownCategories={knownCategories}
            allergenNames={allergenNames} allergenTerms={allergenTerms} linkAllergen={linkAllergen} />
    );

    // Fiche plat en fenêtre modale plutôt qu'en bas de page : auparavant, cliquer sur le crayon d'un plat
    // ouvrait le formulaire APRÈS la liste entière — avec 23 plats, il tombait hors écran et le clic semblait
    // n'avoir aucun effet. Les boutons Annuler/Enregistrer restent collés en bas de la modale pour ne jamais
    // être hors de portée sur un formulaire long. Demande d'Emeni (2026-08-23) : "quand j'ai envie de
    // modifier ce n'est pas pratique".
    // Même fenêtre modale que la fiche plat : le formulaire de formule s'ouvrait sous la liste, donc hors
    // écran dès qu'il y avait quelques formules, et le clic semblait sans effet. Demande d'Emeni
    // (2026-08-25) : « pareil pour formule ».
    // La fenêtre d'édition d'une formule vit dans MenuFormulaForm.tsx.
    const menuModal = menuFormOpen ? (
        <MenuFormulaForm menuForm={menuForm} setMenuForm={setMenuForm} editingMenuId={editingMenuId}
            items={items} savingMenu={savingMenu} resetMenuForm={resetMenuForm} saveMenu={saveMenu}
            addCategoryRow={addCategoryRow} updateCategoryRow={updateCategoryRow}
            removeCategoryRow={removeCategoryRow} moveCategoryRow={moveCategoryRow}
            toggleDishInRow={toggleDishInRow} />
    ) : null;

    const dishModal = dishFormOpen ? (
        <CatalogModalShell titre={editingDishId ? 'Modifier le plat' : 'Nouveau plat'}
            onClose={resetDishForm} onSave={saveDish} saving={savingDish}>
            {dishFormFields}
        </CatalogModalShell>
    ) : null;

    return (
        // Aucune largeur maximale : c'est une grille de cartes, elle gagne à occuper tout l'écran d'un
        // ordinateur. Le plafond de 1152 px laissait un tiers de l'écran vide — signalé par Emeni
        // (2026-08-25). Même parti pris que le suivi des convives.
        // Signalé par Emeni (2026-08-24) : des pages restaient enfermées dans une colonne étroite.
        <div className="space-y-4">
            <div>
                <h1 className="text-xl font-bold text-content flex items-center gap-2"><Utensils size={18} /> Menu & Plats</h1>
                <p className="text-xs text-content-muted mt-1">
                    Configure ici le catalogue de plats et de formules que tu pourras proposer à tes événements — ce catalogue est commun à toutes tes privatisations, indépendamment de la date.
                </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => setTab('plats')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${tab === 'plats' ? 'bg-brand-600 text-brand-fg' : 'bg-surface-soft text-content-muted hover:text-content'}`}>
                    Plats ({items.length})
                </button>
                <button onClick={() => setTab('formules')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${tab === 'formules' ? 'bg-brand-600 text-brand-fg' : 'bg-surface-soft text-content-muted hover:text-content'}`}>
                    Formules ({menus.length})
                </button>
                {/* Réglage rare mais structurant : c'est ici qu'on rattache une préparation maison
                    ("sauce curry") à son allergène (Lait), sans quoi elle n'alerte personne. Placé à côté des
                    onglets plutôt qu'en troisième onglet — on n'y va qu'en constatant qu'un ingrédient manque. */}
                <button onClick={() => navigate('/menu-plats/allergenes')}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold bg-surface-soft text-content-muted hover:text-content flex items-center gap-1.5">
                    <ShieldCheck size={13} /> Allergènes &amp; restrictions
                </button>
                <div className="relative flex-1 min-w-[180px]">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tab === 'formules' ? 'Rechercher une formule...' : 'Rechercher un plat, une catégorie, un ingrédient, une trace...'}
                        className="w-full pl-8 pr-3 py-2 rounded-lg bg-surface-soft border border-border text-sm text-content outline-none focus:border-brand-500" />
                </div>
            </div>

            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
            {importNotice && <p className="text-xs text-sky-700 dark:text-sky-300 font-semibold bg-sky-500/10 border border-sky-500/20 rounded-lg px-2.5 py-2">{importNotice}</p>}

            {/* Filtre par catégorie — réutilise les chips déjà en place sur les suivis Extras et Commandes,
                sans les modifier : même apparence, même comportement, rien de nouveau à apprendre.
                Masqué s'il n'y a qu'une catégorie : filtrer n'aurait alors aucun effet. */}
            {tab === 'plats' && !loading && categoryOptions.length > 1 && (
                <StatusFilterChips options={categoryOptions} value={categoryFilter} onChange={setCategoryFilter} />
            )}

            {tab === 'plats' && !loading && items.length > 0 && <DishBadgeLegend />}

            {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={20} /></div>
            ) : tab === 'plats' ? (
                <div className="space-y-3">
                    {/* Actions en tête de liste : avec 23 plats, « Ajouter un plat » en pied de page obligeait
                        à dérouler tout le catalogue pour en créer un. Demande d'Emeni (2026-08-23). */}
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <Button size="sm" onClick={startAddDish}>
                            <Plus size={13} /> Ajouter un plat
                        </Button>
                        <div className="flex items-center gap-2 flex-wrap">
                            {items.some((i) => (i.variants || []).length > 0) && (
                                <Button variant="secondary" size="sm" onClick={convertVariants} disabled={convertingVariants}>
                                    <RefreshCw size={12} /> {convertingVariants ? 'Conversion...' : 'Convertir les anciennes variantes'}
                                </Button>
                            )}
                            {/* Contrôle d'ensemble : vérifier plat par plat ne suffit pas quand on remplace
                                toute une carte — un ingrédient oublié sur vingt fiches passe inaperçu. */}
                            <Button variant="secondary" size="sm" onClick={() => setImportOpen(true)}>
                                <Upload size={12} /> Importer une carte
                            </Button>
                            <Button variant="secondary" size="sm" onClick={() => setAuditOpen(true)}>
                                <ShieldCheck size={12} /> Contrôler le catalogue
                            </Button>
                            <Button variant="secondary" size="sm" onClick={importTraces} disabled={importingTraces}>
                                <Sparkles size={12} /> {importingTraces ? 'Import en cours...' : 'Appliquer les traces de la carte'}
                            </Button>
                            {items.length > 0 && (
                                <Button variant="secondary" size="sm" onClick={resetCatalog} disabled={resetting}
                                    className="text-red-500! border-red-500/40! hover:bg-red-500/10!">
                                    <Trash2 size={12} /> {resetting ? '...' : 'Vider la carte'}
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Grille plutôt qu'une pile pleine largeur : 27 plats empilés obligeaient à dérouler la
                        carte entière. Mêmes paliers que les fiches Convives. */}
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
                        {filteredItems.length === 0 && <p className="text-sm text-content-muted md:col-span-2 xl:col-span-3 2xl:col-span-4">{items.length === 0 ? "Aucun plat pour l’instant." : (categoryFilter !== 'all' && !search.trim() ? `Aucun plat dans « ${categoryFilter} ».` : "Aucun plat ne correspond à la recherche.")}</p>}
                        {filteredItems.map(renderDishCard)}
                    </div>
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
                        {filteredMenus.length === 0 && !menuFormOpen && <p className="text-sm text-content-muted md:col-span-2 xl:col-span-3 2xl:col-span-4">{menus.length === 0 ? 'Aucune formule pour l\'instant.' : 'Aucune formule ne correspond à la recherche.'}</p>}
                        {filteredMenus.map((menu) => (
                            <FormulaCard key={menu.id} menu={menu}
                                diets={formulaDietBadges(menu.structure, dishById, allergenTerms)}
                                onEdit={startEditMenu} onDelete={deleteMenu} />
                        ))}
                    </div>

                    <button onClick={startAddMenu} className="flex items-center gap-1.5 text-xs font-bold text-brand-600 dark:text-brand-400 hover:underline">
                        <Plus size={13} /> Ajouter une formule
                    </button>
                </div>
            )}

            {dishModal}
            {menuModal}
            {importOpen && <MenuCatalogImport onClose={() => setImportOpen(false)} onDone={load} />}
            {auditOpen && (
                <MenuCatalogAudit dishes={items} allergenTerms={allergenTerms} allergenNames={allergenNames}
                    onLink={linkAllergen} onClose={() => setAuditOpen(false)} />
            )}
        </div>
    );
}
